.frame {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  margin: 0;
  background: linear-gradient(180deg, var(--visual-fg), var(--visual-bg));
  color: var(--text);
  overflow: hidden;
}

.chrome {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 8px 12px;
  background: #151515;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.browserControls {
  display: inline-flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.28);
}

.browserControls svg {
  display: block;
  flex-shrink: 0;
}

.urlbar {
  flex: 1;
  min-width: 0;
  padding: 3px 12px;
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: #181818;
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: var(--radius-pill);
}

.canvas {
  position: relative;
  flex: 1 1 auto;
  padding: clamp(16px, 4%, 28px);
  display: flex;
  align-items: stretch;
  justify-content: stretch;
}

/* When a real image is present, drop the padding so the screenshot
 * fills the frame edge-to-edge. */
.canvasImage {
  padding: 0;
}

.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.labelTag {
  position: absolute;
  top: 14px;
  right: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-muted);
  background: color-mix(in srgb, var(--visual-bg) 70%, transparent);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-pill);
  backdrop-filter: blur(6px);
}

.labelDot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 55%, transparent);
  animation: labelPulse 2s ease-out infinite;
}

@keyframes labelPulse {
  0% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 55%, transparent);
    opacity: 0.9;
  }
  70% {
    box-shadow: 0 0 0 6px color-mix(in srgb, var(--accent) 0%, transparent);
    opacity: 1;
  }
  100% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent);
    opacity: 0.9;
  }
}

@media (prefers-reduced-motion: reduce) {
  .labelDot {
    animation: none;
  }
}

.caption {
  padding: 10px 14px;
  font-size: 12.5px;
  color: var(--text-muted);
  background: color-mix(in srgb, var(--visual-bg) 90%, transparent);
  border-top: 1px solid var(--border);
}

.compact .caption {
  display: none;
}

.bar {
  display: block;
  height: 10px;
  border-radius: var(--radius-pill);
  background: color-mix(in srgb, var(--visual-fg) 70%, #fff);
  opacity: 0.22;
}

/* ---------- CODE motif ---------- */
.code {
  width: 100%;
  font-family: var(--font-mono);
  font-size: 12.5px;
  color: var(--text-muted);
  background: color-mix(in srgb, var(--visual-bg) 92%, #000);
  padding: 14px 16px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border);
  line-height: 1.65;
}

.codeLine {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kw {
  color: #c4a7e7;
}

.id {
  color: #f5e6c8;
}

.fn {
  color: #7dd3fc;
}

.str {
  color: #a7f3d0;
}

.caret::after {
  content: '';
  display: inline-block;
  width: 7px;
  height: 14px;
  background: var(--accent);
  vertical-align: middle;
  animation: blink 1.2s step-end infinite;
}

@keyframes blink {
  50% {
    opacity: 0;
  }
}

/* ---------- DASHBOARD motif ---------- */
.dash {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.dashRow {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.stat {
  padding: 12px;
  background: color-mix(in srgb, var(--visual-bg) 70%, #000);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.statLabel {
  font-family: var(--font-mono);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--text-subtle);
}

.statValue {
  font-size: 18px;
  font-weight: 700;
  color: var(--text);
  letter-spacing: -0.02em;
}

.dashChart {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 6px;
  height: 96px;
  padding: 10px;
  background: color-mix(in srgb, var(--visual-bg) 70%, #000);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
}

.chartBar {
  flex: 1;
  background: linear-gradient(180deg, var(--accent), transparent);
  border-radius: 4px 4px 0 0;
  animation: barRise 900ms var(--ease-out) both;
  transform-origin: bottom;
}

@keyframes barRise {
  from {
    transform: scaleY(0.2);
    opacity: 0;
  }
  to {
    transform: scaleY(1);
    opacity: 1;
  }
}

/* ---------- AUTH motif ---------- */
.auth {
  display: grid;
  place-items: center;
  width: 100%;
}

.authCard {
  width: min(280px, 85%);
  padding: 20px;
  background: color-mix(in srgb, var(--visual-bg) 70%, #000);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: var(--shadow-md);
}

.authTitle {
  font-weight: 700;
  color: var(--text);
  font-size: 15px;
}

.authField {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.authLabel {
  font-family: var(--font-mono);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-subtle);
}

.authButton {
  margin-top: 6px;
  padding: 9px;
  text-align: center;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--accent-contrast);
  background: var(--accent);
  border-radius: var(--radius-sm);
}

/* ---------- EDITOR motif ---------- */
.editor {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  padding: 8px 4px;
}

.barH1 {
  height: 20px !important;
  opacity: 0.55;
  background: var(--accent);
}

.barMeta {
  height: 8px !important;
  opacity: 0.3;
}

/* ---------- FEED motif ---------- */
.feed {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.feedItem {
  display: flex;
  gap: 12px;
  padding: 10px;
  background: color-mix(in srgb, var(--visual-bg) 75%, #000);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
}

.avatar {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent), color-mix(in srgb, var(--accent) 40%, #000));
}

.feedLines {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.barTitle {
  height: 8px !important;
  opacity: 0.5;
  background: var(--accent);
}

/* ---------- CHART motif ---------- */
.chart {
  width: 100%;
  height: 100%;
  color: var(--text-muted);
}
